"use client"; // Add this directive to make the component a client component

import { useState, FormEvent } from 'react';

const FooterNewsletterSection: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // Sanitize email
    const sanitizedEmail = email.trim(); // Remove leading/trailing whitespace

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!sanitizedEmail || !emailRegex.test(sanitizedEmail)) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      let data: { message?: string } | null = null;

      try {
        data = await response.json();
      } catch {
        data = { message: 'Unexpected response from the server' };
      }

      if (response.ok) {
        setStatus('success');
        setMessage(data?.message || 'Thanks for subscribing! Check your email for confirmation.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage(data?.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please try again.');
      console.error('Newsletter subscription error:', error);
    }
  };

  const isLoading = status === 'loading';
  const isDisabled = isLoading || !email.trim();

  return (
    <div className="max-w-md mx-auto text-center">
      <h3 className="mb-4 text-lg font-semibold text-white">Join Our Newsletter</h3>
      <p className="mb-4 text-gray-300">Get updates, free coloring pages and exclusive offers</p>
      <form onSubmit={handleSubmit} className="flex">
        <input
          type="email"
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          className="flex-grow px-4 py-2 text-white border bg-white/10 border-white/20 rounded-l-md focus:outline-none focus:ring-2 focus:ring-pink-400 disabled:opacity-50 disabled:cursor-not-allowed"
          required
        />
        <button
          type="submit"
          disabled={isDisabled}
          className="px-4 py-2 transition-opacity duration-300 bg-gradient-to-r from-pink-500 to-purple-600 rounded-r-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Subscribing...' : 'Subscribe'}
        </button>
      </form>
      {message && (
        <p
          className={`mt-4 text-sm ${
            status === 'success' ? 'text-green-400' : 'text-red-400'
          }`}
        >
          {message}
        </p>
      )}
    </div>
  );
};

export default FooterNewsletterSection;